import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Result, Spin, Button } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { paymentService } from '../services/paymentService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Payment Return Page
 * Handles Stripe redirects after payment processing
 * Confirms payment on backend and redirects user back to dashboard
 */
const PaymentReturn: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const getDashboardRoute = () => {
    if (!user) return '/login';
    const routes: Record<string, string> = {
      owner: '/owner/dashboard',
      advisor: '/advisor/dashboard',
      technician: '/technician/dashboard',
      customer: '/customer/dashboard',
    };
    return routes[user.role] || '/login';
  };
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing your payment...');

  useEffect(() => {
    const processPayment = async () => {
      // Handle both checkout sessions (payment links) and payment intents (modal payments)
      const sessionId = searchParams.get('session_id');
      const paymentIntentId = searchParams.get('payment_intent');
      const redirectStatus = searchParams.get('redirect_status');

      console.log('Payment Return - Query params:', {
        session_id: sessionId,
        payment_intent: paymentIntentId,
        redirect_status: redirectStatus
      });

      // Must have either session_id or payment_intent
      if (!sessionId && !paymentIntentId) {
        console.error('Payment Return - No session_id or payment_intent found');
        setStatus('error');
        setMessage('Payment information not found.');
        return;
      }

      // Check redirect status from Stripe
      if (redirectStatus === 'failed') {
        console.log('Payment Return - Redirect status is failed');
        setStatus('error');
        setMessage('Payment failed. Please try again.');
        return;
      }

      try {
        let result;

        if (sessionId) {
          // Payment link (checkout session)
          console.log('Payment Return - Confirming checkout session:', sessionId);
          result = await paymentService.confirmCheckoutSession(sessionId);
        } else if (paymentIntentId) {
          // Payment modal (payment intent)
          console.log('Payment Return - Confirming payment intent:', paymentIntentId);
          result = await paymentService.confirmPaymentIntent(paymentIntentId);
        }

        console.log('Payment Return - Confirmation result:', result);

        if (result && result.success && (result.status === 'succeeded' || result.status === 'complete')) {
          setStatus('success');
          setMessage('Payment successful! Redirecting you back...');

          // Redirect back to dashboard after 2 seconds
          setTimeout(() => {
            // Try to go back to previous page, or default to dashboard
            const referrer = sessionStorage.getItem('payment_referrer');
            if (referrer) {
              sessionStorage.removeItem('payment_referrer');
              navigate(referrer);
            } else {
              // Default to role-based dashboard
              navigate(getDashboardRoute());
            }
          }, 2000);
        } else {
          console.error('Payment Return - Payment not complete:', result);
          setStatus('error');
          setMessage('Payment processing incomplete. Please contact support.');
        }
      } catch (error: any) {
        console.error('Error confirming payment:', error);
        setStatus('error');
        setMessage(error.response?.data?.error || error.message || 'Failed to confirm payment. Please contact support.');
      }
    };

    processPayment();
  }, [searchParams, navigate]);

  if (status === 'loading') {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            size="large"
          />
          <div style={{ marginTop: 24, fontSize: 16, color: '#666' }}>
            {message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      padding: '24px'
    }}>
      <Result
        status={status === 'success' ? 'success' : 'error'}
        icon={status === 'success' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        title={status === 'success' ? 'Payment Successful!' : 'Payment Failed'}
        subTitle={message}
        extra={
          status === 'error' ? [
            <Button type="primary" key="dashboard" onClick={() => navigate(getDashboardRoute())}>
              Go to Dashboard
            </Button>,
            <Button key="back" onClick={() => navigate(-1)}>
              Go Back
            </Button>
          ] : null
        }
      />
    </div>
  );
};

export default PaymentReturn;
